import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../services/api';
import { useToast } from '../components/shared/ToastNotification';

interface Transaction {
    id: number;
    type: 'invoice' | 'order';
    number: string;
    date: string;
    amount: number;
    status: string;
    balance_due?: number;
}

interface Customer {
    id: number;
    name: string;
    phone: string;
    email: string;
    address: string;
    city: string;
    active: boolean;
    balance: number;
    user_id?: string;
    total_revenue?: number;
    recent_transactions?: Transaction[];
}

function CustomerShow() {
    const { id } = useParams();
    const toast = useToast();
    const [customer, setCustomer] = useState<Customer | null>(null);
    const [transactions, setTransactions] = useState<Transaction[]>([]);
    const [loading, setLoading] = useState(true);
    const [inviting, setInviting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        loadCustomer();
    }, [id]);

    const loadCustomer = async () => {
        try {
            setLoading(true);
            const response = await api.get(`/customers/${id}`);
            if (response.success) {
                setCustomer(response.data);
                setTransactions(response.data.recent_transactions || []);
            } else {
                setError(response.message || 'Failed to load customer');
            }
        } catch (err: any) {
            setError(err.message || 'An error occurred');
        } finally {
            setLoading(false);
        }
    };

    const handleInvite = async () => {
        try {
            setInviting(true);
            const response = await api.post(`/customers/${id}/invite_user`);
            if (response.success) {
                toast.success('Invitation sent successfully');
                // Refresh customer data to show the linked user
                loadCustomer();
            } else {
                toast.error(response.error || 'Failed to invite customer');
            }
        } catch (err: any) {
            console.error(err);
            toast.error(err.message || 'An error occurred during invitation');
        } finally {
            setInviting(false);
        }
    };

    if (loading) return <div className="mt-16 p-8 text-center text-slate-500 font-medium">Loading customer...</div>;
    if (error) return <div className="mt-16 p-8 text-center text-red-500 font-medium">{error}</div>;
    if (!customer) return <div className="mt-16 p-8 text-center text-slate-500 font-medium">Customer not found</div>;

    return (
        <div className="mt-16 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
                <div>
                    <div className="flex items-center gap-3">
                        <Link to="/customers" className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                            </svg>
                        </Link>
                        <h1 className="text-2xl font-bold text-slate-800">{customer.name}</h1>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${customer.active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>
                            {customer.active ? 'Active' : 'Inactive'}
                        </span>
                    </div>
                    <p className="text-slate-500 mt-1 ml-11">{customer.city || 'No city provided'}</p>
                </div>
                <div className="flex items-center gap-3 ml-11 sm:ml-0">
                    <Link to={`/customers/${customer.id}/edit`} className="px-4 py-2 bg-white border border-slate-200 text-slate-700 rounded-xl font-medium hover:bg-slate-50 transition-colors shadow-sm text-sm">
                        Edit
                    </Link>
                    <button className="px-4 py-2 bg-indigo-600 text-white rounded-xl font-medium hover:bg-indigo-700 transition-colors shadow-sm text-sm">
                        New Transaction
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Left Column: Details */}
                <div className="lg:col-span-1 space-y-6">
                    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
                        <h2 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-4">Contact Information</h2>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-xs font-medium text-slate-400 uppercase tracking-wider">Phone</label>
                                <p className="text-slate-700 font-medium">{customer.phone || 'N/A'}</p>
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-slate-400 uppercase tracking-wider">Email</label>
                                <p className="text-slate-700 font-medium">{customer.email || 'N/A'}</p>
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-slate-400 uppercase tracking-wider">Address</label>
                                <p className="text-slate-700 font-medium">{customer.address || 'N/A'}</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-sm font-semibold text-slate-400 uppercase tracking-wider">Portal Access</h2>
                            {customer.user_id ? (
                                <span className="bg-emerald-50 text-emerald-600 text-[10px] font-black px-2 py-1 rounded-md uppercase">Linked</span>
                            ) : (
                                <span className="bg-amber-50 text-amber-600 text-[10px] font-black px-2 py-1 rounded-md uppercase">No Access</span>
                            )}
                        </div>
                        
                        {customer.user_id ? (
                            <div className="space-y-3">
                                <p className="text-xs text-slate-500 font-medium">This client has an active portal account linked to their professional email.</p>
                                <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-xl border border-slate-100">
                                    <span className="text-xl">👤</span>
                                    <div className="overflow-hidden">
                                        <p className="text-xs font-black text-slate-900 truncate">{customer.email}</p>
                                        <p className="text-[10px] font-bold text-slate-400 uppercase">Authorized User</p>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                <p className="text-xs text-slate-500 font-medium leading-relaxed">
                                    Send an invitation to grant this client access to their order history and the digital storefront.
                                </p>
                                <button 
                                    onClick={handleInvite}
                                    disabled={inviting || !customer.email}
                                    className={`w-full py-4 rounded-xl font-black text-[10px] uppercase tracking-widest transition-all flex items-center justify-center gap-2 ${
                                        inviting ? 'bg-slate-100 text-slate-400' : 
                                        !customer.email ? 'bg-slate-50 text-slate-300 cursor-not-allowed' :
                                        'bg-indigo-600 text-white hover:bg-indigo-700 shadow-lg shadow-indigo-100'
                                    }`}
                                >
                                    {inviting ? 'Generating Account...' : (
                                        <>
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" /></svg>
                                            Invite to Portal
                                        </>
                                    )}
                                </button>
                                {!customer.email && (
                                    <p className="text-[10px] text-rose-500 text-center font-bold">Email address is required for invitation.</p>
                                )}
                            </div>
                        )}
                    </div>

                    <div className="bg-indigo-600 rounded-2xl shadow-lg p-6 text-white">
                        <h2 className="text-sm font-semibold opacity-80 uppercase tracking-wider mb-2">Current Balance</h2>
                        <p className="text-3xl font-bold">₹{customer.balance?.toLocaleString()}</p>
                        <div className="mt-4 pt-4 border-t border-white/10 text-xs opacity-80">
                            Total lifetime business: ₹{customer.total_revenue?.toLocaleString() || '0'}
                        </div>
                    </div>
                </div>

                {/* Right Column: Transactions Placeholder */}
                <div className="lg:col-span-2">
                    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden min-h-[400px]">
                        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
                            <h2 className="text-lg font-bold text-slate-800">Recent Transactions</h2>
                            <button className="text-sm font-medium text-indigo-600 hover:text-indigo-700">View all</button>
                        </div>
                        <div className="p-6">
                            {transactions.length === 0 ? (
                                <div className="text-center text-slate-500 font-medium py-8">
                                    <svg className="w-12 h-12 text-slate-200 mb-3 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                                    </svg>
                                    No transactions yet
                                </div>
                            ) : (
                                <div className="divide-y divide-slate-100">
                                    {transactions.map((transaction) => (
                                        <Link
                                            key={`${transaction.type}-${transaction.id}`}
                                            to={`/${transaction.type === 'invoice' ? 'invoices' : 'orders'}/${transaction.id}`}
                                            className="flex items-center justify-between py-4 hover:bg-slate-50 px-4 -mx-4 rounded-lg transition-colors"
                                        >
                                            <div className="flex items-center gap-4">
                                                <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-lg ${transaction.type === 'invoice' ? 'bg-indigo-50 text-indigo-600' : 'bg-emerald-50 text-emerald-600'
                                                    }`}>
                                                    {transaction.type === 'invoice' ? '📄' : '📦'}
                                                </div>
                                                <div>
                                                    <div className="font-bold text-slate-900">
                                                        {transaction.type === 'invoice' ? 'Invoice' : 'Order'} #{transaction.number}
                                                    </div>
                                                    <div className="text-xs text-slate-500">
                                                        {new Date(transaction.date).toLocaleDateString()}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="text-right">
                                                <div className="font-bold text-slate-900">₹{transaction.amount.toLocaleString()}</div>
                                                <div className={`text-xs font-medium capitalize ${transaction.status === 'paid' ? 'text-emerald-600' :
                                                    transaction.status === 'pending' ? 'text-amber-600' :
                                                        transaction.status === 'sent' ? 'text-blue-600' :
                                                            'text-slate-500'
                                                    }`}>
                                                    {transaction.status}
                                                </div>
                                            </div>
                                        </Link>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default CustomerShow;
